/**
 * BasicInfoSection Component
 * Custom layout for basic agent information:
 * - Left: Agent type + Name (stacked)
 * - Right: Output format with schema selector
 * - Bottom: Role definition prompt (full width)
 */

import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useI18n } from '@/i18n';
import { FieldRenderer } from '@/components/fields/FieldRenderer';
import { MERGED_FIELD_CONFIG as FIELD_CONFIG, getConfigValue, ValidationResult } from '@/config/fieldConfig';
import type { AgentConfigNavigationContext } from '@/lib/navigation';

interface BasicInfoSectionProps {
  values: Record<string, any>;
  onChange: (name: string, value: any) => void;
  errors?: Record<string, ValidationResult>;
  highlight?: boolean;
  navigation?: AgentConfigNavigationContext;
}

export const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  values,
  onChange,
  errors = {},
  highlight = false,
  navigation,
}) => {
  const { getSectionTitle, messages } = useI18n();
  const t = messages.agentConfig.basicInfo;

  // Get field schemas
  const agentTypeSchema = FIELD_CONFIG['agent_type'];
  const nameSchema = FIELD_CONFIG['name'];
  const descriptionSchema = FIELD_CONFIG['description'];
  const outputFormatSchema = FIELD_CONFIG['output_format'];

  return (
    <div
      className={`
        bg-white rounded-lg border shadow-sm transition-all duration-300
        ${highlight ? 'border-blue-400 ring-2 ring-blue-200 animate-pulse' : 'border-gray-200'}
      `}
    >
      {/* Section Header */}
      <div
        className={`
          px-4 py-3 border-b border-gray-200
          ${highlight ? 'bg-blue-50' : ''}
        `}
      >
        <h3 className={`text-lg font-semibold ${highlight ? 'text-blue-700' : 'text-gray-800'}`}>
          {getSectionTitle('basic') || 'Basic Information'}
        </h3>
      </div>

      {/* Section Content */}
      <div className="p-4 space-y-4">
        {/* Top Row: Left (Agent Type + Name) | Right (Output Format) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left Column: Agent Type + Name */}
          <div className="space-y-4">
            <FieldRenderer
              name="agent_type"
              value={getConfigValue(values, 'agent_type')}
              onChange={(value) => onChange('agent_type', value)}
              schema={agentTypeSchema}
              error={errors['agent_type']}
              disabled={agentTypeSchema.readonly}
              appContext={values}
            />
            <FieldRenderer
              name="name"
              value={getConfigValue(values, 'name')}
              onChange={(value) => onChange('name', value)}
              schema={nameSchema}
              error={errors['name']}
              appContext={values}
            />
          </div>

          {/* Right Column: Output Format */}
          <div>
            <FieldRenderer
              name="output_format"
              value={getConfigValue(values, 'output_format')}
              onChange={(value) => onChange('output_format', value)}
              schema={outputFormatSchema}
              error={errors['output_format']}
              appContext={values}
              formData={values}
              onFormDataChange={onChange}
            />
          </div>
        </div>

        {/* Bottom Row: Role Definition Prompt (Full Width) */}
        <div>
          <FieldRenderer
            name="description"
            value={getConfigValue(values, 'description')}
            onChange={(value) => onChange('description', value)}
            schema={{
              ...descriptionSchema,
              label: t?.roleDefinitionPrompt || 'Role Definition Prompt',
              help: t?.roleDefinitionHelp || 'Define the agent\'s role and behavioral characteristics',
              overrideI18n: true,
            }}
            error={errors['description']}
            appContext={values}
          />
          {/* Link to Prompts Tab */}
          {navigation && (
            <button
              type="button"
              onClick={navigation.toPrompts}
              className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 hover:underline"
            >
              {t?.configureSystemPrompts || 'Configure System Prompts'}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BasicInfoSection;
